import * as React from 'react';
import { useMemo } from 'react';
import {
  Box,
  Card,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Alert,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import {
  MaterialReactTable,
  MRT_GlobalFilterTextField,
  MRT_ToggleFiltersButton,
  useMaterialReactTable,
  type MRT_ColumnDef,
} from 'material-react-table';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/components/core/v2/toaster';
import dayjs from 'dayjs';

import type { AmenityReservation } from '@/types/v2/reservation';
import {
  getCommunityReservations,
  updateCommunityReservation,
  deleteCommunityReservation
} from '@/api/reservations';
import { useUser } from '@/hooks/v2/use-user';

const statusColors: Record<string, 'default' | 'warning' | 'success' | 'error' | 'info'> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'error',
  cancelled: 'default',
  completed: 'info',
  no_show: 'error',
  waitlisted: 'info',
};

export function AllReservations(): React.JSX.Element {
  const { community, user } = useUser();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['community-reservations', community?.id],
    queryFn: () => getCommunityReservations(community?.id || ''),
    enabled: !!community?.id,
  });

  const reservations = data?.reservations || [];

  // Check if user is a manager/admin
  const isManager = React.useMemo(() => {
    if (!user || !community) return false;

    // Check account-level roles
    const accountRoles = user.account_roles || [];
    const hasAccountRole = accountRoles.some(role =>
      ['account_owner', 'account_admin', 'account_manager'].includes(role.name)
    );

    // Check community-level roles
    const communityRoles = community.roles || [];
    const hasCommunityRole = communityRoles.includes('community_manager');

    return hasAccountRole || hasCommunityRole;
  }, [user, community]);

  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = React.useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [selectedReservation, setSelectedReservation] = React.useState<AmenityReservation | null>(null);

  // Edit form state
  const [editForm, setEditForm] = React.useState({
    start_time: '',
    end_time: '',
    purpose: '',
    special_requests: '',
    guest_count: '',
  });

  // Form validation errors
  const [editErrors, setEditErrors] = React.useState<Record<string, string>>({});

  // Mutations
  const updateMutation = useMutation({
    mutationFn: ({ reservationId, data }: { reservationId: string; data: any }) =>
      updateCommunityReservation(community?.id || '', reservationId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['community-reservations'] });
      queryClient.invalidateQueries({ queryKey: ['pending-reservations'] });
      toast.success('Reservation updated successfully');
      setEditDialogOpen(false);
      setSelectedReservation(null);
    },
    onError: (error: any) => {
      const responseData = error?.response?.data;

      // Handle validation errors (422 status)
      if (error?.response?.status === 422 && responseData?.errors) {
        setEditErrors(responseData.errors);
        toast.error('Please fix the validation errors below');
      } else {
        // Handle other errors
        toast.error(responseData?.message || 'Failed to update reservation');
        setEditErrors({});
      }
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (reservationId: string) =>
      deleteCommunityReservation(community?.id || '', reservationId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['community-reservations'] });
      queryClient.invalidateQueries({ queryKey: ['pending-reservations'] });
      toast.success('Reservation deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedReservation(null);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Failed to delete reservation');
    },
  });

  // Handler functions
  const handleEdit = (reservation: AmenityReservation) => {
    setSelectedReservation(reservation);
    setEditForm({
      start_time: dayjs.utc(reservation.start_time).local().format('YYYY-MM-DD HH:mm:ss'),
      end_time: dayjs.utc(reservation.end_time).local().format('YYYY-MM-DD HH:mm:ss'),
      purpose: reservation.purpose || '',
      special_requests: reservation.special_requests || '',
      guest_count: reservation.guest_count?.toString() || '',
    });
    setEditErrors({});
    setEditDialogOpen(true);
  };

  const handleDelete = (reservation: AmenityReservation) => {
    setSelectedReservation(reservation);
    setDeleteDialogOpen(true);
  };

  const handleEditSubmit = () => {
    if (!selectedReservation) return;

    const data: any = {};
    if (isManager && editForm.start_time !== dayjs.utc(selectedReservation.start_time).local().format('YYYY-MM-DD HH:mm:ss')) {
      data.start_time = dayjs(editForm.start_time).utc().format('YYYY-MM-DD HH:mm:ss');
    }
    if (isManager && editForm.end_time !== dayjs.utc(selectedReservation.end_time).local().format('YYYY-MM-DD HH:mm:ss')) {
      data.end_time = dayjs(editForm.end_time).utc().format('YYYY-MM-DD HH:mm:ss');
    }
    if (editForm.purpose !== selectedReservation.purpose) {
      data.purpose = editForm.purpose;
    }
    if (editForm.special_requests !== selectedReservation.special_requests) {
      data.special_requests = editForm.special_requests;
    }
    if (editForm.guest_count !== selectedReservation.guest_count?.toString()) {
      data.guest_count = editForm.guest_count ? parseInt(editForm.guest_count) : null;
    }

    updateMutation.mutate({ reservationId: selectedReservation.id, data });
    setEditDialogOpen(false);
  };

  const handleDeleteConfirm = () => {
    if (!selectedReservation) return;
    deleteMutation.mutate(selectedReservation.id);
  };

  const columns = useMemo<MRT_ColumnDef<AmenityReservation>[]>(
    () => {
      const baseColumns: MRT_ColumnDef<AmenityReservation>[] = [
        {
          accessorKey: 'amenity.name',
          header: 'Amenity',
          size: 150,
        },
        {
          accessorFn: (row) => `${row.user?.first_name} ${row.user?.last_name}`,
          header: 'Resident',
          size: 150,
        },
        {
          accessorKey: 'start_time',
          header: 'Start Time',
          size: 180,
          Cell: ({ cell }) => dayjs.utc(cell.getValue<string>()).local().format('MMM D, YYYY h:mm A'),
        },
        {
          accessorKey: 'end_time',
          header: 'End Time',
          size: 120,
          Cell: ({ cell }) => dayjs.utc(cell.getValue<string>()).local().format('h:mm A'),
        },
        {
          accessorKey: 'status',
          header: 'Status',
          size: 120,
          Cell: ({ cell }) => (
            <Chip
              label={cell.getValue<string>().replace('_', ' ').toUpperCase()}
              size="small"
              color={statusColors[cell.getValue<string>()]}
              sx={{ fontWeight: 500 }}
            />
          ),
        },
        {
          accessorKey: 'purpose',
          header: 'Purpose',
          size: 200,
          Cell: ({ cell }) => cell.getValue<string>() || '—',
        },
        {
          accessorKey: 'created_at',
          header: 'Requested',
          size: 120,
          Cell: ({ cell }) => dayjs(cell.getValue<string>()).format('MMM D, YYYY'),
        },
      ];

      if (isManager) {
        baseColumns.push({
          id: 'actions',
          header: 'Actions',
          size: 120,
          Cell: ({ row }: any) => (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton
                size="small"
                onClick={() => handleEdit(row.original)}
                color="primary"
                disabled={dayjs.utc(row.original.start_time).local().isBefore(dayjs())}
                title={dayjs.utc(row.original.start_time).local().isBefore(dayjs()) ? "Cannot edit past reservations" : "Edit reservation"}
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => handleDelete(row.original)}
                color="error"
                disabled={dayjs.utc(row.original.start_time).local().isBefore(dayjs())}
                title={dayjs.utc(row.original.start_time).local().isBefore(dayjs()) ? "Cannot delete past reservations" : "Delete reservation"}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ),
        });
      }

      return baseColumns;
    },
    [isManager],
  );

  const table = useMaterialReactTable<AmenityReservation>({
    columns,
    data: reservations,
    initialState: {
      showGlobalFilter: true,
      density: 'compact',
      sorting: [
        {
          id: 'start_time',
          desc: true,
        },
      ],
    },
    muiSearchTextFieldProps: {
      size: 'small',
      variant: 'outlined',
    },
    state: {
      isLoading,
    },
  });

  return (
    <>
      <Card>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', p: '0px 4px', alignItems: 'center' }}>
          <MRT_GlobalFilterTextField table={table} />
          <MRT_ToggleFiltersButton table={table} />
        </Box>

        <MaterialReactTable table={table} />
      </Card>

      {/* Edit Dialog */}
    <Dialog open={editDialogOpen} onClose={() => {
      setEditDialogOpen(false);
      setEditErrors({});
    }} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Reservation</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {isManager && (
            <>
              <DateTimePicker
                label="Start Time"
                value={dayjs(editForm.start_time)}
                onChange={(newValue) => {
                  if (newValue) {
                    setEditForm(prev => ({ ...prev, start_time: newValue.format('YYYY-MM-DD HH:mm:ss') }));
                    // Clear error when user starts typing
                    if (editErrors.start_time) {
                      setEditErrors(prev => ({ ...prev, start_time: '' }));
                    }
                  }
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: Boolean(editErrors.start_time),
                    helperText: editErrors.start_time,
                  },
                }}
              />
              <DateTimePicker
                label="End Time"
                value={dayjs(editForm.end_time)}
                onChange={(newValue) => {
                  if (newValue) {
                    setEditForm(prev => ({ ...prev, end_time: newValue.format('YYYY-MM-DD HH:mm:ss') }));
                    // Clear error when user starts typing
                    if (editErrors.end_time) {
                      setEditErrors(prev => ({ ...prev, end_time: '' }));
                    }
                  }
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: Boolean(editErrors.end_time),
                    helperText: editErrors.end_time,
                  },
                }}
              />
            </>
          )}
          <TextField
            label="Purpose"
            multiline
            rows={2}
            value={editForm.purpose}
            onChange={(e) => {
              setEditForm(prev => ({ ...prev, purpose: e.target.value }));
              // Clear error when user starts typing
              if (editErrors.purpose) {
                setEditErrors(prev => ({ ...prev, purpose: '' }));
              }
            }}
            fullWidth
            error={Boolean(editErrors.purpose)}
            helperText={editErrors.purpose}
          />
          <TextField
            label="Special Requests"
            multiline
            rows={3}
            value={editForm.special_requests}
            onChange={(e) => {
              setEditForm(prev => ({ ...prev, special_requests: e.target.value }));
              // Clear error when user starts typing
              if (editErrors.special_requests) {
                setEditErrors(prev => ({ ...prev, special_requests: '' }));
              }
            }}
            fullWidth
            error={Boolean(editErrors.special_requests)}
            helperText={editErrors.special_requests}
          />
          <TextField
            label="Guest Count"
            type="number"
            value={editForm.guest_count}
            onChange={(e) => {
              setEditForm(prev => ({ ...prev, guest_count: e.target.value }));
              // Clear error when user starts typing
              if (editErrors.guest_count) {
                setEditErrors(prev => ({ ...prev, guest_count: '' }));
              }
            }}
            fullWidth
            inputProps={{ min: 0 }}
            error={Boolean(editErrors.guest_count)}
            helperText={editErrors.guest_count}
          />
          {selectedReservation && (
            <Alert severity="info" sx={{ mt: 1 }}>
              <strong>Resident:</strong> {selectedReservation.user?.first_name} {selectedReservation.user?.last_name}
              <br />
              <strong>Amenity:</strong> {selectedReservation.amenity?.name}
              <br />
              <strong>Time:</strong> {isManager ?
                `${dayjs(editForm.start_time).format('MMM D, YYYY h:mm A')} - ${dayjs(editForm.end_time).format('h:mm A')}` :
                `${dayjs.utc(selectedReservation.start_time).local().format('MMM D, YYYY h:mm A')} - ${dayjs.utc(selectedReservation.end_time).local().format('h:mm A')}`}
              {isManager && (
                <>
                  <br />
                  <em>Note: As a manager, you can edit the reservation times above.</em>
                </>
              )}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
        <Button
          onClick={handleEditSubmit}
          variant="contained"
          disabled={updateMutation.isPending}
        >
          {updateMutation.isPending ? 'Updating...' : 'Update Reservation'}
        </Button>
      </DialogActions>
    </Dialog>

    {/* Delete Confirmation Dialog */}
    <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
      <DialogTitle>Delete Reservation</DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This action cannot be undone. The reservation will be permanently deleted.
          </Alert>
          {selectedReservation && (
            <Box>
              <strong>Reservation Details:</strong>
              <br />
              <strong>Resident:</strong> {selectedReservation.user?.first_name} {selectedReservation.user?.last_name}
              <br />
              <strong>Amenity:</strong> {selectedReservation.amenity?.name}
              <br />
              <strong>Time:</strong> {dayjs.utc(selectedReservation.start_time).local().format('MMM D, YYYY h:mm A')} - {dayjs.utc(selectedReservation.end_time).local().format('h:mm A')}
              <br />
              <strong>Status:</strong> {selectedReservation.status?.toUpperCase()}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
        <Button
          onClick={handleDeleteConfirm}
          variant="contained"
          color="error"
          disabled={deleteMutation.isPending}
        >
          {deleteMutation.isPending ? 'Deleting...' : 'Delete Reservation'}
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
}





